/**
 * builders/occurrences-builder.js — constructor PURO del FormData multipart que espera el
 * backend offline de Occurrences (POST occurrences/save-offline-answers-copy ->
 * OfflineControllerCopy@saveOffline). Re-implementacion LIMPIA del v1
 * src/offline/modules/occurrences-builder.js (REFERENCIA — NO importado).
 *
 * PUREZA (PUSH-05): cero SQL, cero transport, cero import de v1 ni del plugin. El handler
 * (modules/occurrences.js) HIDRATA las filas de answers/addons desde SQLite y se las pasa
 * ya leidas; este modulo SOLO moldea el multipart y REPORTA que fotos omitio (sin tocar SQL).
 *
 * ╔═ INVARIANTE CERO-PERDIDA DE FOTOS (CAP-photo) ═══════════════════════════════════════════╗
 * ║ NUNCA re-enviar una respuesta/addon de FOTO sin su binario de forma que el server          ║
 * ║ SOBRESCRIBA una imagen ya subida.                                                          ║
 * ║                                                                                            ║
 * ║ CAUSA RAIZ del bug de perdida: N fotos en una occurrence = N filas outbox                  ║
 * ║ `occurrence.syncOccurrence` (client_uuid distinto por item). El handler RE-LEE             ║
 * ║ answers+addons WHERE occurrence_id y reconstruye TODO el batch en CADA op. La purga        ║
 * ║ (purge.js) borra el binario en cuanto el op DUENO de esa foto queda done. Una fila         ║
 * ║ hermana — u otra re-edicion / close->reopen->editar — re-drena DESPUES con el binario ya   ║
 * ║ purgado. Si el builder mandaba la foto "solo-ref" (value/answer_ref sin file), el server   ║
 * ║ sobrescribia la imagen ya subida -> FOTO PERDIDA.                                          ║
 * ║                                                                                            ║
 * ║ COMPORTAMIENTO DEL BACKEND (verificado en OfflineControllerCopy@saveOffline):              ║
 * ║  - ANSWERS: `Answer::updateOrCreate([item_id, faloc_id], $answer)` hace FILL PARCIAL — solo║
 * ║    escribe las columnas presentes en $answer. La URL de la foto vive en la columna `value` ║
 * ║    (image_ref NO es fillable del modelo Answer -> el server lo IGNORA). Por tanto OMITIR el ║
 * ║    campo `value` CONSERVA la URL ya guardada; current_points/total_points SI se mandan      ║
 * ║    (una re-calificacion del item persiste sin tocar la foto). Mandar value='' o la ref      ║
 * ║    SOBRESCRIBIRIA la imagen -> JAMAS hacerlo cuando el binario no esta.                     ║
 * ║  - ADDONS: el server hace `$addon->answer = answer_ref|image_ref|null` de forma             ║
 * ║    INCONDICIONAL. Omitir campos lo pondria a null -> hay que OMITIR EL ADDON-FOTO COMPLETO  ║
 * ║    del batch para que el server no lo toque y conserve la imagen previa (un addon-foto no   ║
 * ║    tiene otro dato que perder: su unico contenido es la imagen).                            ║
 * ║                                                                                            ║
 * ║ "binario no resuelto" = resolveBlob(record) devuelve null. Eso cubre AMBOS canales: web    ║
 * ║ (idb por image_ref) y nativo (filesystem por local_path) — el backend de fotos devuelve    ║
 * ║ null si la foto fue subida+purgada O si el binario fue evictado/corrompido. NO se usa       ║
 * ║ `local_path` como senal de "foto presente" (una ruta puede existir con el archivo borrado).║
 * ╚════════════════════════════════════════════════════════════════════════════════════════════╝
 *
 * Cinturones de seguridad portados del v1:
 *   - ref-as-value: si un record de foto trae `value` con base64 crudo (`data:image...`) o es
 *     item-foto con binario resuelto, se manda `image_ref` COMO value, NUNCA el base64 crudo.
 *   - objetos/arrays (form-reading {last,current,difference}, form-checkbox/multinumeric []) ->
 *     JSON.stringify, NUNCA String() (que colapsaria a "[object Object]" y perderia el dato).
 */

import { serializeExtraProperties } from './extra-properties'

const isAddonRecord = (record) =>
  record.type &&
  (record.type.includes('picture') || record.type === 'comment' || record.type === 'document' ||
    record.type === 'video') &&
  record.type !== 'photo'

/** Addon-foto: porta un binario de imagen. */
const isPictureAddon = (record) => !!record.type && record.type.includes('picture')

/** Addon-ARCHIVO: un `document` que capturó un binario (PDF/doc) -> viaja como multipart `file` con su
 * FILENAME real (guardado en `answer`), igual que las fotos. Un `document` SIN image_ref es texto/URL. */
const isFileAddon = (record) => record.type === 'document' && !!record.image_ref

// ── CHUNKING DEL BATCH (fix pérdida de fotos >20) ────────────────────────────────────────────────
// El endpoint recibe TODAS las fotos de la occurrence en UN multipart. PHP `max_file_uploads` (default
// 20) DESCARTA EN SILENCIO los file-parts que exceden el límite, y el controller guarda los que sí
// llegaron y responde 200 -> con >20 fotos, las que sobran NUNCA llegaban a S3 y se marcaban `done`
// (reproducido: 50 fotos -> primeras 20 OK, últimas 30 en 404). Se trocea el batch en requests bajo el
// límite (nº de archivos y peso). Con <= MAX_FILES archivos devuelve UN chunk (comportamiento idéntico).
export const MAX_FILES_PER_CHUNK = 15 // margen bajo el 20 de PHP (por si el batch trae otros file-parts)
export const MAX_BYTES_PER_CHUNK = 8 * 1024 * 1024 // margen bajo post_max_size típico
// Tope por Nº DE REGISTROS: CADA record (answer o addon) aporta ~6-8 variables POST (answers[N][item_id],
// [value], [current_points], [total_points], [occurrence_id]... + extra_properties). PHP `max_input_vars`
// (default 1000) DESCARTA EN SILENCIO las variables que exceden el límite y responde 200 -> con muchas
// respuestas (>~167 con 6 vars c/u) se PERDÍAN las ÚLTIMAS (reproducido: 186 answers -> server persiste
// 167, se pierden las posiciones 168..186). El chunking por archivos NO cubría esto (el texto no cuenta
// como file). 100 registros × ~8 vars = 800 < 1000, con margen para extra_properties + metadata.
export const MAX_RECORDS_PER_CHUNK = 100

/** ¿este record ADJUNTARÁ un file-part? Conservador: se evalúa ANTES de resolver el binario (si luego
 * no resuelve, el builder lo omite -> menos archivos reales, siempre por debajo del límite).
 *
 * `skipRefs` (TKT-2240) = image_refs cuya subida YA está confirmada: el handler las salta en su
 * resolveBlob, así que tampoco cuentan para el límite de archivos/peso del chunk. Sin esto, una
 * occurrence con 50 fotos ya subidas seguía troceándose en chunks de "archivos" que en realidad
 * viajaban vacíos (mismos requests, sin binario). */
const potentiallyCarriesFile = (record, skipRefs) => {
  const ref = record.image_ref || record.answer_ref
  if (skipRefs && ref && skipRefs.has(ref)) return false
  if (record.type) return (isPictureAddon(record) && !!(record.image_ref || record.answer_ref)) || isFileAddon(record)
  return !record.type && !!record.image_ref
}

/**
 * Trocea los records en grupos cuyo nº de archivos/peso NO exceda el límite por request. Los records
 * SIN archivo (respuestas de texto) no cuentan para el límite y viajan con el chunk en curso.
 * @param {Array<object>} records
 * @param {Object<string,number>} sizeByRef  image_ref -> bytes (photo_meta.byte_length); 0 si desconocido.
 * @param {{ maxFiles?: number, maxBytes?: number, maxRecords?: number, skipFileRefs?: Set<string> }} opts
 *        `skipFileRefs` = refs cuyo binario NO se va a adjuntar (subida ya confirmada, TKT-2240).
 * @returns {Array<Array<object>>} chunks. Con <= MAX_FILES archivos -> [[...records]] (un solo chunk).
 */
export const chunkRecordsForUpload = (records, sizeByRef = {}, opts = {}) => {
  const maxFiles = opts.maxFiles || MAX_FILES_PER_CHUNK
  const maxBytes = opts.maxBytes || MAX_BYTES_PER_CHUNK
  const maxRecords = opts.maxRecords || MAX_RECORDS_PER_CHUNK
  const skipRefs = opts.skipFileRefs || null
  const chunks = []
  let cur = []
  let curFiles = 0
  let curBytes = 0
  for (const r of records) {
    const isFile = potentiallyCarriesFile(r, skipRefs)
    const sz = isFile ? (sizeByRef[r.image_ref] || 0) : 0
    // Cierra el chunk actual si:
    //  (a) añadir este ARCHIVO excedería el límite de archivos/peso (max_file_uploads/post_max_size), O
    //  (b) el chunk ya alcanzó el tope de REGISTROS (cada record aporta variables POST; max_input_vars
    //      descartaba en silencio la cola -> se perdían las últimas respuestas con occurrences grandes).
    // Siempre se deja al menos un record en el chunk (nunca cierra vacío; un archivo que por sí solo
    // excede el peso igual se manda para no bloquearlo).
    const wouldExceedFiles = isFile && curFiles > 0 && (curFiles + 1 > maxFiles || curBytes + sz > maxBytes)
    const wouldExceedRecords = cur.length > 0 && cur.length + 1 > maxRecords
    if (wouldExceedFiles || wouldExceedRecords) {
      chunks.push(cur)
      cur = []
      curFiles = 0
      curBytes = 0
    }
    cur.push(r)
    if (isFile) {
      curFiles++
      curBytes += sz
    }
  }
  if (cur.length) chunks.push(cur)
  return chunks.length ? chunks : [records]
}

/**
 * Construye el FormData multipart de un batch de records hidratados (answers + addons).
 *
 * @param {Array<object>} records  filas de answers/addons ya leidas de SQLite por el handler.
 * @param {string} appVersion
 * @param {Function} resolveBlob   async (record) => Blob|null  (lee el binario del backend de fotos
 *                                  activo; null = ya subido+purgado o evictado).
 * @returns {Promise<{ formData: FormData, omittedPhotoRefs: Array<{ ref: string|null, kind: 'answer'|'addon', itemId: number }>, attachedPhotoRefs: string[] }>}
 *   `omittedPhotoRefs` = fotos cuyo binario NO se resolvio y por eso se OMITIERON del envio (sin
 *   sobrescribir nada en el server). El handler las clasifica via photo_meta (purgada-OK vs
 *   evictada-perdida) para dejar evidencia. El builder NO consulta photo_meta: se mantiene PURO.
 *   `attachedPhotoRefs` = las refs cuyo binario SI quedo dentro de este multipart (TKT-2240).
 */
export const buildBatchFormData = async (records, appVersion, resolveBlob) => {
  const formData = new FormData()
  let answerIndex = 0
  let addonIndex = 0
  const addonFileMap = []
  const answerFileMap = []
  const omittedPhotoRefs = []

  for (const ans of records) {
    if (isAddonRecord(ans)) {
      // ── ADDON ──────────────────────────────────────────────────────────────
      // Porta binario SOLO si hay una ref que resolver: una foto BORRADA (image_ref null explícito,
      // answer '') no es "binario perdido" -> debe viajar como answer vacío para que el server la limpie.
      const carriesBinary = (isPictureAddon(ans) && !!(ans.image_ref || ans.answer_ref)) || isFileAddon(ans)
      let addonBlob = null
      if (carriesBinary) {
        // Resolver el binario UNA vez (decide envio + adjunto, sin doble I/O). Vale para foto Y archivo.
        addonBlob = await resolveBlob(ans)
        if (!addonBlob) {
          // CERO-PERDIDA (addon): binario ausente. El server setea $addon->answer de forma
          // INCONDICIONAL -> re-enviarlo sobrescribiria la imagen/archivo con la ref/null. Omitir el
          // addon COMPLETO -> el server no lo procesa -> conserva el binario ya subido.
          omittedPhotoRefs.push({ ref: ans.image_ref || ans.answer_ref || null, kind: 'addon', itemId: ans.item_id })
          continue
        }
      }

      formData.append(
        `addons_values[${addonIndex}][occurrence_id]`,
        String(ans.occurrence_id || ans.form_assignment_location_occurrence_id)
      )
      formData.append(`addons_values[${addonIndex}][item_id]`, String(ans.item_id))
      formData.append(`addons_values[${addonIndex}][type]`, ans.type)

      if (!carriesBinary) {
        // Addon SIN binario -> answer: comment/document de texto o URL, video (delete = ''), y foto
        // BORRADA (answer '' limpia el addon en server).
        // TKT-2157/TKT-2280: NO colapsar null/undefined -> '' . El '' EXPLICITO es intencion de
        // borrado; null/undefined = "no tocado" y debe OMITIRSE la llave para que el server lo trate
        // como ABSENT y no destruya contenido no tocado (prerequisito del backend real DELETE gateado).
        if (ans.answer !== null && ans.answer !== undefined) {
          formData.append(`addons_values[${addonIndex}][answer]`, ans.answer)
        }
      } else {
        // foto O archivo (document con binario) -> answer_ref (el binario viaja como file abajo).
        formData.append(`addons_values[${addonIndex}][answer_ref]`, ans.answer_ref || ans.image_ref || '')
      }

      if (addonBlob) {
        // filename real para el archivo (document): el backend deriva la extensión S3 de ahí. Foto -> null.
        addonFileMap.push({
          index: addonIndex,
          blob: addonBlob,
          ref: ans.answer_ref || ans.image_ref,
          // `ref` de arriba es lo que el server espera como answer_ref y da nombre al file-part,
          // pero en un addon-foto NO coincide con la PK de photo_meta: answer_ref es una clave
          // propia del cliente (ans-<epoch>-<item>) y la fila de la foto vive bajo image_ref. Para
          // sellar la subida hace falta esta otra (TKT-2240).
          photoRef: ans.image_ref || ans.answer_ref,
          key: `${ans.item_id}:${ans.type}`,
          filename: isFileAddon(ans) ? ans.answer : null
        })
      }
      addonIndex++
    } else {
      // ── ANSWER ─────────────────────────────────────────────────────────────
      formData.append(`answers[${answerIndex}][occurrence_id]`, String(ans.occurrence_id))
      formData.append(
        `answers[${answerIndex}][form_assignment_location_occurrence_id]`,
        String(ans.form_assignment_location_occurrence_id ?? ans.occurrence_id)
      )
      formData.append(`answers[${answerIndex}][item_id]`, String(ans.item_id))

      const hasPhotoRef = !ans.type && !!ans.image_ref
      // Resolver el binario AHORA. Un answer SIN image_ref jamas resuelve (no es foto). null =
      // binario ya subido+purgado o evictado, en AMBOS canales (web idb / nativo FS).
      const photoBlob = hasPhotoRef ? await resolveBlob(ans) : null
      const photoResolved = !!photoBlob
      // Caso legacy: la foto llega como base64 crudo dentro de `value` (no via PhotoStore). En ese
      // caso el binario VIVE en `value`, asi que NO es una foto "sin binario" -> no se omite.
      const valueIsRawBase64 = typeof ans.value === 'string' && ans.value.startsWith('data:image')

      if (hasPhotoRef && !photoResolved && !valueIsRawBase64) {
        // CERO-PERDIDA (answer): foto cuyo binario NO esta. OMITIR value + image_ref + file ->
        // updateOrCreate hace fill PARCIAL, asi que NO mandar `value` CONSERVA la URL ya subida.
        // current_points/total_points SI viajan (abajo) -> una re-calificacion del item persiste
        // sin tocar la foto. NUNCA mandar value (ni '' ni la ref): eso sobrescribiria la imagen.
        omittedPhotoRefs.push({ ref: ans.image_ref, kind: 'answer', itemId: ans.item_id })
      } else {
        // Camino normal: foto con binario resuelto (1er envio / re-edicion con foto nueva), base64
        // legacy, o respuesta NO-foto. Comportamiento IDENTICO al previo al fix.
        const sendRefAsValue = (hasPhotoRef && photoResolved) || (hasPhotoRef && valueIsRawBase64)
        const rawValue = sendRefAsValue ? ans.image_ref : ans.value
        let valueToSend = rawValue ?? ''
        // objetos/arrays -> JSON (String() colapsaria a "[object Object]" y perderia el dato).
        if (rawValue !== null && typeof rawValue === 'object') {
          valueToSend = JSON.stringify(rawValue)
        }
        formData.append(`answers[${answerIndex}][value]`, String(valueToSend))

        // La REFERENCIA de foto solo viaja cuando su binario viaja (o base64 legacy). El backend
        // igual la ignora (image_ref no es fillable), pero omitirla es lo coherente con el invariante.
        if (ans.image_ref && (photoResolved || valueIsRawBase64)) {
          formData.append(`answers[${answerIndex}][image_ref]`, ans.image_ref)
        }
        if (hasPhotoRef && photoResolved) {
          answerFileMap.push({ index: answerIndex, blob: photoBlob, ref: ans.image_ref })
        }
      }

      // current_points/total_points SIEMPRE viajan (tambien en el camino de foto-omitida): el
      // score del item se sincroniza sin depender del binario.
      formData.append(`answers[${answerIndex}][current_points]`, String(ans.current_points ?? 0))
      formData.append(`answers[${answerIndex}][total_points]`, String(ans.total_points ?? 0))

      const extraProps = serializeExtraProperties(ans.extra_properties)
      if (extraProps !== null) {
        formData.append(`answers[${answerIndex}][extra_properties]`, extraProps)
      }
      if (ans.created_at) {
        formData.append(`answers[${answerIndex}][created_at]`, String(ans.created_at))
      }
      if (ans.updated_at) {
        formData.append(`answers[${answerIndex}][updated_at]`, String(ans.updated_at))
      }

      answerIndex++
    }
  }

  formData.append('app_version', appVersion ?? '')

  // Refs cuyo binario SÍ quedó dentro de este multipart. El caller las sella en photo_meta cuando el
  // chunk responde 2xx (TKT-2240): esa marca —y no la fecha de un op— es la única evidencia honesta
  // de que la foto viajó, y es lo que decide si el siguiente batch puede saltársela.
  const attachedPhotoRefs = []
  const attachedByKey = new Map()

  // Binarios de addon (foto o archivo; un addon sin binario ya fue OMITIDO completo). Para un ARCHIVO
  // se usa su filename real (extensión S3 correcta); para una foto, `${ref}.jpeg`.
  for (const { index, blob, ref, photoRef, filename, key } of addonFileMap) {
    if (blob) {
      formData.append(`addons_values[${index}][file]`, blob, filename || `${ref}.jpeg`)
      if (photoRef) {
        attachedPhotoRefs.push(photoRef)
        attachedByKey.set(key, photoRef)
      }
    }
  }

  // Binarios de answer-foto (resueltos arriba; un answer sin binario va sin value/image_ref/file).
  for (const { index, blob, ref } of answerFileMap) {
    if (blob) {
      formData.append(`answers[${index}][file]`, blob, `${ref}.jpeg`)
      if (ref) attachedPhotoRefs.push(ref)
    }
  }

  return { formData, omittedPhotoRefs, attachedPhotoRefs, attachedByKey }
}
